import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { findings as findingsApi } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ShieldCheck, AlertCircle, Camera, Upload, Save, FileX,
  Check, Clock, ListTodo, X as XIcon, Loader2, Languages,
} from "lucide-react";
import { localizedCategory } from "../lib/i18nContent";

const STATUSES = [
  { v: "to_do", icon: ListTodo, classes: "is-active todo" },
  { v: "in_progress", icon: Clock, classes: "is-active inprog" },
  { v: "done", icon: Check, classes: "is-active done" },
  { v: "cannot_do", icon: XIcon, classes: "is-active cannot" },
];

export default function PublicFindingPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState(0);
  const [notes, setNotes] = useState("");
  const [author, setAuthor] = useState("");
  const [statusVal, setStatusVal] = useState("to_do");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [descTranslated, setDescTranslated] = useState("");
  const [noteTranslated, setNoteTranslated] = useState("");
  const [translatingDesc, setTranslatingDesc] = useState(false);
  const [translatingNote, setTranslatingNote] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await findingsApi.publicView(token);
      setData(r);
      const f = r.finding;
      setNotes(f.follow_up_notes || "");
      setStatusVal(f.status || "to_do");
    } catch (e) {
      setErrorCode(e?.response?.status || 0);
      setError(e?.response?.data?.detail || t("public.unavailable"));
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const persistStatus = async (newStatus) => {
    setStatusVal(newStatus);
    try {
      await findingsApi.publicUpdate(token, {
        status: newStatus,
        follow_up_notes: notes,
        update_author: author,
      });
      toast.success(t("public_finding.saved"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("public_finding.save_error"));
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await findingsApi.publicUpdate(token, {
        status: statusVal,
        follow_up_notes: notes,
        update_author: author,
      });
      toast.success(t("public_finding.saved"));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("public_finding.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const onPickFile = () => fileRef.current?.click();
  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await findingsApi.publicUpload(token, file);
      toast.success(t("public_finding.saved"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("public_finding.upload_error"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const translateText = async (kind) => {
    // kind: "desc" | "note". Target = current UI language so an English manager
    // viewing a Spanish note gets English on demand (and vice-versa).
    const source = kind === "desc" ? (data?.finding?.description || "") : notes;
    if (!source.trim()) return;
    const target = i18n.language === "es" ? "es" : "en";
    const setBusy = kind === "desc" ? setTranslatingDesc : setTranslatingNote;
    const setResult = kind === "desc" ? setDescTranslated : setNoteTranslated;
    setBusy(true);
    try {
      const r = await findingsApi.publicTranslateNote(token, source, target);
      setResult(r.translated || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("public_finding.translate_error"));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <Card className="canales-card p-10 max-w-md text-center" data-testid="public-finding-error">
          <FileX size={36} className="mx-auto text-stone-400" />
          <h1 className="font-display text-2xl mt-4">
            {errorCode === 410 ? t("public.expired") : t("public.unavailable")}
          </h1>
          <p className="text-sm text-stone-500 mt-2">{error}</p>
        </Card>
      </div>
    );
  }

  if (!data) {
    return <div className="p-10 text-stone-500">{t("common.loading")}</div>;
  }

  const { finding, audit } = data;
  const photos = finding.photos && finding.photos.length > 0
    ? finding.photos
    : (finding.photo_url ? [{ url: finding.photo_url }] : []);

  const severityColor =
    finding.severity === "Alta"
      ? "text-red-600"
      : finding.severity === "Media"
      ? "text-amber-600"
      : "text-stone-500";

  return (
    <div className="min-h-screen bg-white" data-testid="public-finding">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-[#1E3A8A] grid place-items-center text-white">
              <ShieldCheck size={16} />
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 leading-none">{t("brand")}</div>
              <div className="font-display text-sm leading-tight">{t("public_finding.title")}</div>
            </div>
          </div>
          <span className="pill text-[10px]">{audit?.audit_number || finding.store_name}</span>
        </div>
      </header>

      {/* Hero */}
      <section className="hero-night text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
          <div className="canales-overline text-stone-300">{finding.store_name}</div>
          <h1 className="font-display text-3xl md:text-4xl mt-2 leading-tight" data-testid="pf-description">
            {descTranslated || finding.description}
          </h1>
          {descTranslated && (
            <p className="text-[11px] text-amber-200 mt-2 italic">
              {t("public_finding.translated")} →{" "}
              <button
                type="button"
                onClick={() => setDescTranslated("")}
                className="underline hover:text-amber-100"
                data-testid="pf-desc-show-original"
              >
                {t("public_finding.show_original")}
              </button>
            </p>
          )}
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              onClick={() => translateText("desc")}
              disabled={translatingDesc}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-300 hover:text-amber-200 underline underline-offset-2"
              data-testid="pf-translate-desc"
            >
              {translatingDesc ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
              {i18n.language === "es" ? t("public_finding.translate_desc_es") : t("public_finding.translate_desc_en")}
            </button>
          </div>
          <p className="text-stone-300 text-sm mt-3 max-w-xl">
            {t("public_finding.subtitle_role")}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="pill bg-white/10 text-white text-[11px]">
              {t("public_finding.category")}: {localizedCategory(finding.category, i18n.language)}
            </span>
            <span className="pill bg-white/10 text-white text-[11px]">
              <AlertCircle size={11} className={`inline mr-1 ${severityColor}`} />
              {t("public_finding.severity")}: {t(`severity.${finding.severity}`, finding.severity)}
            </span>
            {audit?.template && (
              <span className="pill bg-white/10 text-white text-[11px]">
                {audit.template}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Status buttons */}
        <Card className="canales-card p-5">
          <div className="canales-overline">{t("findings.col_status")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {STATUSES.map((s) => {
              const Icon = s.icon;
              const active = statusVal === s.v;
              return (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => persistStatus(s.v)}
                  className={`status-toggle py-3 text-sm flex items-center justify-center gap-1.5 ${active ? s.classes : ""}`}
                  data-testid={`pf-status-${s.v}`}
                >
                  <Icon size={15} />
                  <span>{t(`finding_status.${s.v}`)}</span>
                </button>
              );
            })}
          </div>
          {finding.last_updated_by && (
            <p className="text-[11px] text-stone-500 mt-3">
              {t("public_finding.last_updated_by", { by: finding.last_updated_by })}
              {finding.updated_at && ` · ${t("public_finding.last_updated", { when: new Date(finding.updated_at).toLocaleString() })}`}
            </p>
          )}
        </Card>

        {/* Notes */}
        <Card className="canales-card p-5">
          <div className="flex items-center justify-between">
            <div className="canales-overline">{t("public_finding.notes")}</div>
            {notes.trim() && (
              <button
                type="button"
                onClick={() => translateText("note")}
                disabled={translatingNote}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-700 hover:text-blue-900 underline underline-offset-2"
                data-testid="pf-translate-note"
              >
                {translatingNote ? <Loader2 size={12} className="animate-spin" /> : <Languages size={12} />}
                {i18n.language === "es" ? t("public_finding.translate_note_es") : t("public_finding.translate_note_en")}
              </button>
            )}
          </div>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("public_finding.notes_placeholder")}
            className="mt-2 min-h-[120px]"
            data-testid="pf-notes"
          />
          {noteTranslated && (
            <div className="mt-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-stone-800" data-testid="pf-note-translated">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                  {t("public_finding.translated")}
                </span>
                <button
                  type="button"
                  onClick={() => setNoteTranslated("")}
                  className="text-[11px] underline text-stone-600 hover:text-stone-900"
                  data-testid="pf-note-show-original"
                >
                  {t("public_finding.show_original")}
                </button>
              </div>
              {noteTranslated}
            </div>
          )}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="canales-overline">{t("public_finding.author_label")}</label>
              <Input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder={t("public_finding.author_placeholder")}
                className="mt-1"
                data-testid="pf-author"
              />
            </div>
            <Button
              onClick={saveNotes}
              disabled={saving}
              className="brand-button gap-1.5 h-10"
              data-testid="pf-save-notes"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t("public_finding.save")}
            </Button>
          </div>
        </Card>

        {/* Photos */}
        <Card className="canales-card p-5">
          <div className="flex items-center justify-between">
            <div className="canales-overline">{t("public_finding.photos")} ({photos.length})</div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={onFileSelected}
              data-testid="pf-file-input"
            />
            <Button
              onClick={onPickFile}
              disabled={uploading}
              className="brand-button-accent gap-1.5"
              data-testid="pf-upload-button"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {t("public_finding.upload_photo")}
            </Button>
          </div>
          {photos.length === 0 ? (
            <p className="text-sm text-stone-400 mt-4 flex items-center gap-2">
              <Camera size={16} /> {t("public_finding.no_photos")}
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
              {photos.map((p, i) => (
                <a
                  key={i}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square rounded-md border border-stone-200 overflow-hidden bg-stone-50 hover:scale-[1.02] transition-transform"
                >
                  <img src={p.url} alt={p.original_filename || `photo-${i}`} className="w-full h-full object-cover" />
                </a>
              ))}
            </div>
          )}
        </Card>
      </div>

      <footer className="border-t border-stone-200 py-6 text-center text-xs text-stone-500">
        © {t("brand")} · {t("public_finding.title")}
      </footer>
    </div>
  );
}
